import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Heading } from 'grommet';

import { GET_WORK_SESSIONS_STATE } from 'src/gql/workSessionsAPI';
import useSetInterval from 'src/hooks/useSetInterval';
import { updateState, updateTick } from 'src/reducers/workSessions/workSessionsReducer';
import FullPageSpinner from 'src/components/FullPageSpinner';
import { StoredWorkSessionsState } from 'src/models/workSessions';
import { useAuthorizedQuery } from 'src/utils/useAuthorizedQuery';
import EstimatedEndTime from './components/EstimatedEndTime';
import Controls from './components/Controls';
import SessionProgress from './components/SessionProgress';
import SettingsMenu from './components/SettingsMenu';
import { useNotifications } from './hooks/useNotifications';
import { useSyncWorkSessionsState } from './hooks/useSyncWorkSessionsState';

interface GQLReturnData {
  workSessionsState: StoredWorkSessionsState;
}

function WorkSessionsPage(): JSX.Element {
  const dispatch = useDispatch();
  const { loading, called } = useAuthorizedQuery<GQLReturnData>(GET_WORK_SESSIONS_STATE, {
    onCompleted: ({ workSessionsState }) => dispatch(updateState(workSessionsState)),
  });
  const tick = useCallback(() => dispatch(updateTick()), [dispatch]);

  useSetInterval(tick, 1000);
  useNotifications();
  useSyncWorkSessionsState(called && !loading);

  if (loading) {
    return <FullPageSpinner isLoading />;
  }

  return (
    <Box direction='column' gap='small'>
      <Box direction='row' justify='between' align='center'>
        <Heading level={1} margin='none'>
          Work Sessions
        </Heading>
        <SettingsMenu />
      </Box>
      <Box direction='row' justify='between' align='center'>
        <Controls />
        <EstimatedEndTime />
      </Box>
      <SessionProgress />
    </Box>
  );
}

export default WorkSessionsPage;
